/*
  Nische About page — the motion layer.

  Everything here is enhancement. The page is complete and readable with this
  file blocked, missing or erroring: the hidden-before-reveal state is applied
  BY THIS SCRIPT (it adds `nab-js` to the section), never by the stylesheet, so
  no-JS can never leave content invisible. That is the one rule this file must
  keep.

  Four behaviours:
    1. reveal   - media wipes in and copy rises as it enters the viewport
    2. count    - the numbers band counts up the first time it is seen
    3. tilt     - the hero collage drifts with the pointer, by depth
    4. pause    - both marquees stop while hovered or focused

  All four are skipped when the visitor asks for reduced motion, and the tilt is
  skipped on touch, where there is no pointer to follow.
*/
(function () {
  if (window.NischeAbout) return;
  window.NischeAbout = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (!root || root.dataset.nabReady === 'true') return;
    root.dataset.nabReady = 'true';

    /* Arming the reveal from JS is what makes it safe: the stylesheet only
       hides `.nab-js .nab-reveal`, and `nab-js` cannot exist without this
       script having run. */
    if (!reduced && root.dataset.nabReveal === 'true') {
      root.classList.add('nab-js');
      reveal(root);
    }

    if (!reduced && root.dataset.nabCount === 'true') count(root);
    if (!reduced && root.dataset.nabTilt === 'true') tilt(root);
  }

  /* ---------------------------------------------------------------- reveal */

  function reveal(root) {
    var targets = root.querySelectorAll('.nab-reveal');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      /* No observer, no reveal — but the content must still be visible. */
      root.classList.remove('nab-js');
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          /* Stagger siblings so a row of cards arrives as a sequence rather
             than as one block. The delay is per group, read off the element,
             so the CSS decides the rhythm and this file only counts. */
          var i = parseInt(entry.target.dataset.nabIndex || '0', 10);
          entry.target.style.transitionDelay = i * 90 + 'ms';
          entry.target.classList.add('nab-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );

    Array.prototype.forEach.call(targets, function (el) {
      io.observe(el);
    });
  }

  /* ----------------------------------------------------------------- count */

  function count(root) {
    var nums = root.querySelectorAll('[data-nab-count]');
    if (!nums.length || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          run(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    Array.prototype.forEach.call(nums, function (el) {
      io.observe(el);
    });

    function run(el) {
      var final = el.textContent.trim();
      var target = parseInt(final.replace(/\D/g, ''), 10);
      if (!isFinite(target)) return;

      /* A year counting up from zero reads as a broken odometer, so anything
         four digits or longer starts just below itself instead. */
      var from = target >= 1000 ? target - 25 : 0;
      var start = null;
      var dur = 1100;

      /* The element keeps its real text for anyone not watching the animation
         — a screen reader on the settled page reads the final value, and the
         exact original string (suffixes and all) is restored at the end. */
      el.setAttribute('aria-label', final);

      function frame(t) {
        if (start === null) start = t;
        var p = Math.min((t - start) / dur, 1);
        /* easeOutCubic: fast, then settles. */
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = String(Math.round(from + (target - from) * eased));
        if (p < 1) {
          requestAnimationFrame(frame);
        } else {
          el.textContent = final;
          el.removeAttribute('aria-label');
        }
      }

      requestAnimationFrame(frame);
    }
  }

  /* ------------------------------------------------------------------ tilt */

  function tilt(root) {
    var hero = root.querySelector('.nab-hero');
    var items = root.querySelectorAll('.nab-collage__item');
    if (!hero || !items.length) return;
    /* A coarse pointer has nothing to follow, and reading pointer position on
       touch only fights the scroll. */
    if (!window.matchMedia('(pointer: fine)').matches) return;

    var frame = null;
    var pending = null;

    hero.addEventListener('mousemove', function (e) {
      var r = hero.getBoundingClientRect();
      pending = {
        x: (e.clientX - r.left) / r.width - 0.5,
        y: (e.clientY - r.top) / r.height - 0.5,
      };
      if (frame) return;
      frame = requestAnimationFrame(apply);
    });

    hero.addEventListener('mouseleave', function () {
      pending = { x: 0, y: 0 };
      if (frame) return;
      frame = requestAnimationFrame(apply);
    });

    function apply() {
      frame = null;
      if (!pending) return;
      Array.prototype.forEach.call(items, function (el, i) {
        /* Different depths, so the stack separates instead of sliding as one
           flat picture. The rotation stays in CSS — this only translates —
           because CSS owns each item's resting angle. */
        var depth = 10 + i * 7;
        el.style.translate =
          (pending.x * depth).toFixed(1) + 'px ' + (pending.y * depth).toFixed(1) + 'px';
      });
    }
  }

  /* ------------------------------------------------------------------ boot */

  function boot() {
    document.querySelectorAll('.nische-about').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The theme editor re-renders a section into a fresh DOM node on every
     settings change, so the new copy needs arming again. */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector ? e.target.querySelector('.nische-about') : null;
    if (el) init(el);
  });
})();
